// feature/litisoft-export — LITISOFT monthly export view.
// feature/litisoft-preview — adds a "Vista previa en vivo" table with the same 18 columns;
// insumos role gets preview-only (no Excel download).
import React, { useEffect, useMemo, useState } from "react";
import { exportsApi, cases as casesApi } from "../../api/endpoints";
import { apiBase } from "../../api/client";
import Spinner from "../../components/Spinner";
import { useToast } from "../../components/Toast";
import { useAuth } from "../../auth/AuthContext";
import { rolesOf } from "../../utils/permissions";
// feature/remanente-export — Sprint P3.
import RemanenteExport from "../../components/admin/RemanenteExport";
// feature/informes-mensuales-v1 — sprint84.
import InformeMensualPanel from "./InformeMensualPanel";
// feature/remanentes-pendientes — "Remanentes del mes" (casos sin respuesta).
import RemanentesMesPanel from "./RemanentesMesPanel";

const TIPOS = [
  ["tutela",        "Tutelas"],
  ["fallo",         "Fallos"],
  ["requerimiento", "Requerimientos"],
  ["desacato",      "Desacatos"],
  ["sancion",       "Sanciones"],
];

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Exports() {
  const toast = useToast();
  const { user } = useAuth();
  // feature/single-role-audit — multi-role aware.
  // feature/litisoft-role — sprint87. Cristian (litisoft) needs the month-end
  // download too, plus inline number/radicado completion.
  const canDownload = ["admin", "reparto", "litisoft"].some(r => rolesOf(user).includes(r));
  const canEditLitisoft = ["admin", "litisoft"].some(r => rolesOf(user).includes(r));
  const [month, setMonth] = useState(currentMonth());
  const [tiposSel, setTiposSel] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  // feature/litisoft-preview — live preview table state
  const [livePreview, setLivePreview] = useState(null);
  const [liveBusy, setLiveBusy] = useState(false);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  // feature/litisoft-completion — sprint87. Preview filter: all | incompletos | pendiente.
  const [filterMode, setFilterMode] = useState("all");

  // Column index → editable LITISOFT field. Matches HEADERS order:
  // col 2 (idx 1) = Número Litisoft; col 25 (idx 24) = Radicado Litisoft.
  const EDITABLE_COL = { 1: "litisoft_numero", 24: "litisoft_radicado" };

  // Persist an inline edit, then patch local state so the cell + the
  // "Pendiente Litisoft" counter update without a full reload.
  function onCellSaved(caseId, colIdx, field, value) {
    setLivePreview(prev => {
      if (!prev) return prev;
      const rows = prev.rows.map(r => {
        if (r.case_id !== caseId) return r;
        const cells = [...r.cells];
        cells[colIdx] = value;
        const next = { ...r, cells, [field]: value };
        if (field === "litisoft_numero") next.pendiente_litisoft = !value;
        return next;
      });
      const pendiente_litisoft = rows.filter(r => r.pendiente_litisoft).length;
      return { ...prev, rows, counts: { ...prev.counts, pendiente_litisoft } };
    });
  }

  function loadLive() {
    setLiveBusy(true);
    exportsApi.litisoftJson(params)
      .then(r => setLivePreview(r.data))
      .catch(() => toast.push("Error al cargar la vista previa", "error"))
      .finally(() => setLiveBusy(false));
  }

  const params = useMemo(() => {
    const out = { month };
    if (tiposSel.length) out.tipo = tiposSel.join(",");
    return out;
  }, [month, tiposSel]);

  function loadPreview() {
    setBusy(true); setPreview(null);
    exportsApi.litisoftPreview(params)
      .then(r => setPreview(r.data))
      .catch(() => toast.push("Error al cargar la vista previa", "error"))
      .finally(() => setBusy(false));
  }
  useEffect(() => { setPreview(null); setLivePreview(null); setFilterMode("all"); }, [month, tiposSel]);

  const sortedRows = useMemo(() => {
    if (!livePreview) return [];
    if (sortCol == null) return livePreview.rows;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...livePreview.rows].sort((a, b) => {
      const av = a.cells[sortCol], bv = b.cells[sortCol];
      const an = Number(av), bn = Number(bv);
      if (Number.isFinite(an) && Number.isFinite(bn)) return (an - bn) * dir;
      return String(av || "").localeCompare(String(bv || ""), "es") * dir;
    });
  }, [livePreview, sortCol, sortDir]);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  }

  // feature/litisoft-completion — sprint87. Apply the preview filter on top
  // of the sort. "incompletos" = sprint83-era data-quality check (unchanged);
  // "pendiente" = missing LITISOFT número (Cristian's worklist).
  const visibleRows = useMemo(() => {
    if (filterMode === "incompletos") return sortedRows.filter(r => r.incomplete);
    if (filterMode === "pendiente")   return sortedRows.filter(r => r.pendiente_litisoft);
    return sortedRows;
  }, [sortedRows, filterMode]);

  async function download() {
    setBusy(true);
    try {
      const token = localStorage.getItem("eds.token");
      const r = await fetch(apiBase + exportsApi.litisoftUrl(params), {
        headers: { Authorization: "Bearer " + token },
      });
      if (!r.ok) throw new Error("HTTP " + r.status);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `LITISOFT_EDS_${month}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.push("Excel descargado", "success");
    } catch (e) {
      toast.push("Error al descargar el archivo", "error");
    } finally { setBusy(false); }
  }

  function toggleTipo(t) {
    setTiposSel(arr => arr.includes(t) ? arr.filter(x => x !== t) : [...arr, t]);
  }

  // feature/remanente-export — Sprint P3. Multi-role gate (admin /
  // carpetas / directora) via user.roles[]. The carpetas team needs
  // this surface but doesn't get the LITISOFT view.
  const remanenteRoles = (user?.roles && user.roles.length) ? user.roles : (user?.role ? [user.role] : []);
  const canRemanente = ["admin", "carpetas", "directora"].some(r => remanenteRoles.includes(r));
  // LITISOFT view is for admin/reparto/insumos/directora — same as
  // the route's original allowlist, minus carpetas. Pure-carpetas
  // users now reach this page via the sidebar for REMANENTE only.
  const canLitisoftView = ["admin", "reparto", "insumos", "directora", "litisoft"].some(r => remanenteRoles.includes(r));
  // sprint84 — Informe Mensual (Contrato 225-2025) panel. Admin +
  // directora only; pure carpetas/reparto/insumos don't need it.
  const canInformeMensual = ["admin", "directora"].some(r => remanenteRoles.includes(r));

  return (
    <div className="col" style={{ gap: 24, maxWidth: 960 }}>
      {canInformeMensual && <InformeMensualPanel />}
      {canInformeMensual && <RemanentesMesPanel />}
      {canRemanente && <RemanenteExport />}

      {canLitisoftView && (<>
      <h1 style={{ margin: 0 }}>Exportar a LITISOFT</h1>
      <div className="muted" style={{ marginTop: -8 }}>
        Descarga la plantilla mensual con los casos del mes para el responsable de LITISOFT.
      </div>

      <div className="card">
        <div className="grid-2">
          <div>
            <label>Mes</label>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </div>
          <div>
            <label>Tipos de caso (opcional)</label>
            <div className="row" style={{ flexWrap: "wrap", gap: 6 }}>
              {TIPOS.map(([v, l]) => {
                const on = tiposSel.includes(v);
                return (
                  <span key={v}
                        className={"badge " + (on ? "mint" : "")}
                        style={{ cursor: "pointer", border: on ? undefined : "1px solid var(--border-hairline)" }}
                        onClick={() => toggleTipo(v)}>
                    {l}
                  </span>
                );
              })}
              {tiposSel.length > 0 && <button className="ghost sm" onClick={() => setTiposSel([])}>Limpiar</button>}
            </div>
          </div>
        </div>

        <div className="row" style={{ marginTop: 16, gap: 8 }}>
          <button className="secondary" onClick={loadPreview} disabled={busy}>
            {busy && !preview && <Spinner />} Vista previa
          </button>
          {canDownload && (
            <button onClick={download} disabled={busy}>
              {busy && <Spinner color="currentColor" />} Descargar Excel
            </button>
          )}
        </div>

        {preview && (
          <div className="card tight" style={{ marginTop: 16, background: "var(--bg-subtle)" }}>
            <div style={{ fontSize: 14 }}>
              <strong>{preview.total}</strong> caso(s) serán exportados para <strong>{preview.month}</strong>.
              {preview.incomplete > 0 && (
                <> {preview.incomplete} {preview.incomplete === 1 ? "tiene" : "tienen"} campos LITISOFT incompletos.</>
              )}
              {/* feature/litisoft-completion — sprint87. Separate worklist counter. */}
              {preview.pendiente_litisoft > 0 && (
                <> {preview.pendiente_litisoft} pendiente{preview.pendiente_litisoft === 1 ? "" : "s"} de registro LITISOFT (sin número).</>
              )}
            </div>
            {preview.incomplete > 0 && (
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Los campos faltantes (fecha_siniestro, placa, numero_siniestro, departamento, municipio, temática)
                aparecerán vacíos en el Excel — puede completarlos antes de descargar.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card tight" style={{ background: "var(--bg-subtle)" }}>
        <div className="muted" style={{ fontSize: 12, lineHeight: 1.6 }}>
          El campo <strong>Número LITISOFT</strong> queda vacío — lo completa el responsable de subir a LITISOFT.<br />
          Las columnas posteriores a <strong>Temática</strong> (impugnación, segunda instancia, desacato) son rellenadas
          directamente en LITISOFT.
        </div>
      </div>

      {/* feature/litisoft-preview — live preview table */}
      <div className="card" style={{ maxWidth: "none" }}>
        <div className="row" style={{ alignItems: "center" }}>
          <h2 style={{ margin: 0 }}>Vista previa en vivo</h2>
          <div style={{ flex: 1 }} />
          {livePreview && (
            <button className="ghost sm" onClick={loadLive} title="Refrescar">↻</button>
          )}
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Mismo formato que el Excel (18 columnas, mayúsculas, fechas en español, formato de cédula).
          Las filas con campos LITISOFT incompletos aparecen con fondo ámbar.
        </div>
        <div style={{ marginTop: 12 }}>
          {!livePreview ? (
            liveBusy ? (
              /* feature/ui-polish-motion — skeleton sized like the real
                 table while the preview loads (capped at 8 rows; never
                 one per eventual row). */
              <div aria-busy="true" aria-label="Cargando vista previa">
                <div className="skeleton" style={{ width: "38%", height: 14, marginBottom: 10 }} />
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="skeleton"
                       style={{ display: "block", width: "100%", height: 26, marginBottom: 6, opacity: 1 - i * 0.09 }} />
                ))}
              </div>
            ) : (
              <button className="secondary" onClick={loadLive}>
                Generar vista previa
              </button>
            )
          ) : (
            <>
              <div style={{ marginBottom: 8, fontSize: 13, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span>Casos: <strong>{livePreview.counts.total}</strong></span>
                {/* feature/litisoft-completion — sprint87. Two independent
                    filter toggles + counts: "incompletos" (data quality) and
                    "Pendiente Litisoft" (missing número — Cristian's queue). */}
                <FilterPill active={filterMode === "all"} onClick={() => setFilterMode("all")}>
                  Todos
                </FilterPill>
                <FilterPill active={filterMode === "incompletos"} onClick={() => setFilterMode("incompletos")} tone="amber">
                  Solo incompletos ({livePreview.counts.incomplete || 0})
                </FilterPill>
                <FilterPill active={filterMode === "pendiente"} onClick={() => setFilterMode("pendiente")} tone="blue">
                  Pendiente LITISOFT ({livePreview.counts.pendiente_litisoft || 0})
                </FilterPill>
                <span className="muted" style={{ marginLeft: "auto" }}>{visibleRows.length} mostrado(s)</span>
              </div>
              <div style={{ overflow: "auto", maxHeight: 520, border: "1px solid var(--border-hairline)", borderRadius: 8 }}>
                <table style={{ minWidth: 1400, fontSize: 12 }}>
                  <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                    <tr>
                      {livePreview.headers.map((h, idx) => (
                        <th key={idx} onClick={() => toggleSort(idx)} style={{ cursor: "pointer", whiteSpace: "pre-line", verticalAlign: "top" }}>
                          {h} {sortCol === idx ? (sortDir === "asc" ? "▲" : "▼") : ""}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map(r => (
                      <tr key={r.case_id}
                          style={r.incomplete ? { background: "var(--color-warning-bg)" } : {}}
                          title={r.incomplete ? "Campos LITISOFT incompletos" : ""}>
                        {r.cells.map((cell, idx) => {
                          const editField = canEditLitisoft ? EDITABLE_COL[idx] : null;
                          if (editField) {
                            return (
                              <td key={idx} style={{ padding: 2 }}>
                                <LitisoftCell
                                  caseId={r.case_id}
                                  field={editField}
                                  value={cell || ""}
                                  onSaved={(v) => onCellSaved(r.case_id, idx, editField, v)}
                                  onError={(msg) => toast.push(msg, "error")}
                                />
                              </td>
                            );
                          }
                          return (
                            <td key={idx}>
                              {cell === "" || cell == null ? <span className="muted">—</span> : cell}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
      </>)}
    </div>
  );
}

// feature/litisoft-completion — sprint87. Small filter toggle pill for the
// preview (Todos / Solo incompletos / Pendiente Litisoft).
function FilterPill({ active, onClick, tone, children }) {
  const toneColor = tone === "amber" ? "var(--color-warning-ink)" : tone === "blue" ? "var(--color-info)" : "var(--text-body)";
  return (
    <button type="button" onClick={onClick}
            className={active ? "" : "secondary"}
            style={{ padding: "2px 10px", fontSize: 12, color: active ? undefined : toneColor }}>
      {children}
    </button>
  );
}

// feature/litisoft-completion — sprint87. Inline-editable LITISOFT cell.
// Click → input; blur or Enter → PATCH the single field via cases.update;
// shows a saving spinner then a brief ✓. No Excel round-trip.
function LitisoftCell({ caseId, field, value, onSaved, onError }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [status, setStatus] = useState("idle"); // idle | saving | saved
  useEffect(() => { setDraft(value); }, [value]);

  async function commit() {
    setEditing(false);
    const next = draft.trim();
    if (next === (value || "")) return; // no-op
    setStatus("saving");
    try {
      await casesApi.update(caseId, { [field]: next || null });
      setStatus("saved");
      onSaved?.(next);
      // feature/ui-polish-motion — matches the .cell-save-indicator 2.2s
      // fade-out animation in index.css so the ✓ fades instead of popping.
      setTimeout(() => setStatus("idle"), 2400);
    } catch (e) {
      setStatus("idle");
      setDraft(value); // revert
      onError?.(e?.response?.data?.error || e?.response?.data?.message || "No se pudo guardar");
    }
  }

  if (editing) {
    return (
      <input autoFocus value={draft}
             onChange={(e) => setDraft(e.target.value)}
             onBlur={commit}
             onKeyDown={(e) => {
               if (e.key === "Enter") { e.preventDefault(); commit(); }
               else if (e.key === "Escape") { setDraft(value); setEditing(false); }
             }}
             style={{ width: "100%", minWidth: 90, padding: "2px 4px", fontSize: 12 }} />
    );
  }
  return (
    <div onClick={() => setEditing(true)}
         title="Clic para editar"
         style={{ cursor: "text", minHeight: 18, padding: "2px 4px", borderRadius: 4,
                  border: "1px dashed transparent", display: "flex", alignItems: "center", gap: 4 }}
         onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--border-hairline)"}
         onMouseLeave={(e) => e.currentTarget.style.borderColor = "transparent"}>
      {value ? value : <span className="muted">— editar —</span>}
      {status === "saving" && <Spinner size={10} />}
      {status === "saved" && <span className="cell-save-indicator saved" style={{ fontWeight: 700 }}>✓</span>}
    </div>
  );
}
